import {
  Alert,
  AnimatedContentLoader,
  Badge,
  Button,
  Input,
  Pagination,
  Select,
  TextTooltip
} from '@components'
import { useBusinessContext } from '@context'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { useChangeLog, useChangeLogActors } from '@query'
import {
  ChangeLogAction,
  ChangeLogResourceType,
  IChangeLogEntry,
  IChangeLogResource
} from '@query/changeLog'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFilter, FaQuestionCircle, FaShieldAlt, FaSyncAlt, FaUndo } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { formatDate } from '@utils/formatDate'
import { FieldChange, getChangeLogResourceUrl } from './ChangeLogFieldChange'
import { humanizeChangeLogValue, humanizeResourceTypeSlug } from './changeLogHumanize'

const PAGE_SIZE = 25

/**
 * Change log rows before this date may be incomplete.
 * Show the legacy notice only when the current page includes such rows.
 */
const CHANGE_LOG_RELIABLE_FROM = '2026-08-20'

const predatesReliableChangeLog = (occurredAt: string): boolean =>
  dayjs(occurredAt).isBefore(CHANGE_LOG_RELIABLE_FROM)

const RESOURCE_TYPES: Array<ChangeLogResourceType | 'all'> = [
  'all',
  'document',
  'document_relation',
  'entry',
  'business',
  'invoice',
  'invoice_row',
  'purchase_invoice',
  'period',
  'vat_period',
  'business_usership',
  'account',
  'contact',
  'attachment',
  'tag',
  'workflow'
]

const ACTIONS: Array<ChangeLogAction | 'all'> = ['all', 'create', 'update', 'delete']

const ACTION_BADGE_TYPE: Record<ChangeLogAction, 'success' | 'info' | 'error'> = {
  create: 'success',
  update: 'info',
  delete: 'error'
}

const formatOccurredAt = (value: string): string => dayjs(value).format('L LT')

const REL_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60]
]

/** Localized relative ago; seconds are bucketed to 10s steps to avoid a live ticker. */
const formatRelativeAgo = (timestampMs: number, nowMs: number, lang: string): string | 'now' => {
  const diffSec = Math.round((timestampMs - nowMs) / 1000)
  const abs = Math.abs(diffSec)
  const sign = diffSec <= 0 ? -1 : 1
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'always' })

  if (abs < 60) {
    const bucketed = Math.floor(abs / 10) * 10
    if (bucketed === 0) return 'now'
    return rtf.format(sign * bucketed, 'second')
  }

  for (const [unit, secs] of REL_UNITS) {
    if (abs >= secs) return rtf.format(Math.round(diffSec / secs), unit)
  }
  return rtf.format(Math.round(diffSec / 60), 'minute')
}

// Table rows and filter options prefer email over name.
const actorEmailFirst = (entry: IChangeLogEntry, systemLabel: string): string => {
  return entry.actor.email || entry.actor.name || systemLabel
}

const CLIENT_I18N: Record<string, { label: string; tooltip: string }> = {
  'nocfo-web': {
    label: 'settings.changeLog.clients.web',
    tooltip: 'settings.changeLog.clients.tooltips.web'
  },
  'nocfo-mobile': {
    label: 'settings.changeLog.clients.mobile',
    tooltip: 'settings.changeLog.clients.tooltips.mobile'
  },
  'nocfo-api': {
    label: 'settings.changeLog.clients.api',
    tooltip: 'settings.changeLog.clients.tooltips.api'
  },
  'nocfo-mcp': {
    label: 'settings.changeLog.clients.mcp',
    tooltip: 'settings.changeLog.clients.tooltips.mcp'
  },
  'nocfo-agent': {
    label: 'settings.changeLog.clients.agent',
    tooltip: 'settings.changeLog.clients.tooltips.agent'
  }
}

const ResourceLabel: React.FC<{ slug: string; resource: IChangeLogResource }> = ({
  slug,
  resource
}) => {
  const [t] = useTranslation()
  const label = humanizeChangeLogValue(resource.label, t) ?? resource.label
  const href = getChangeLogResourceUrl(slug, resource.link ?? resource)
  if (!href) return <>{label}</>
  return <ObjectLink to={href}>{label}</ObjectLink>
}

// Prefer email so filter options stay unique when names collide.
const actorLabel = (
  actor: { id: number | null; name: string | null; email: string | null },
  systemLabel: string
): string => actor.email || actor.name || systemLabel

const LegacyHistoryNotice: React.FC = () => {
  const [t] = useTranslation()
  const reliableFromLabel = formatDate(CHANGE_LOG_RELIABLE_FROM)

  return (
    <LegacyHistoryNoticeBanner>
      <Alert
        type="warning"
        inline
        title={t('settings.changeLog.legacyNotice.title')}
        description={t('settings.changeLog.legacyNotice.description', {
          date: reliableFromLabel
        })}
        marginBottom={0}
      />
    </LegacyHistoryNoticeBanner>
  )
}

const ChangeLogSettings: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const [t, i18n] = useTranslation()
  const { slug } = useBusinessContext()
  const [resourceType, setResourceType] = useState<ChangeLogResourceType | 'all'>('all')
  const [action, setAction] = useState<ChangeLogAction | 'all'>('all')
  const [actorId, setActorId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [now, setNow] = useState(() => Date.now())
  // Track last known page count so we can clamp the *requested* page before
  // keepPreviousData would otherwise flash rows from an out-of-range page.
  const knownTotalPagesRef = React.useRef(1)

  const requestPage = Math.min(page, knownTotalPagesRef.current)

  const { data: actors = [], refetch: refetchActors } = useChangeLogActors()

  const { data, isLoading, isFetching, isError, dataUpdatedAt, refetch, isPlaceholderData } =
    useChangeLog({
      resourceType,
      action,
      actorId,
      search,
      from: from || undefined,
      to: to || undefined,
      page: requestPage,
      pageSize: PAGE_SIZE
    })

  React.useEffect(() => {
    if (dataUpdatedAt <= 0 || isFetching) return
    setNow(Date.now())

    // Relative "Updated …" only needs sub-minute ticks while data is <60s old;
    // use a 10s interval so we don't re-render the whole table every second.
    if (Date.now() - dataUpdatedAt >= 60_000) return

    const id = window.setInterval(() => {
      setNow(Date.now())
      if (Date.now() - dataUpdatedAt >= 60_000) {
        window.clearInterval(id)
      }
    }, 10_000)
    return () => window.clearInterval(id)
  }, [dataUpdatedAt, isFetching])

  const handleRefresh = () => {
    void refetch()
    void refetchActors()
  }

  const hasLoadedOnce = dataUpdatedAt > 0
  const statusLabel = (() => {
    if (!hasLoadedOnce) {
      if (isLoading) return t('settings.changeLog.loading')
      if (isFetching) return t('settings.changeLog.updating')
      return null
    }
    if (isFetching) return t('settings.changeLog.updating')
    const relative = formatRelativeAgo(dataUpdatedAt, now, i18n.language)
    if (relative === 'now') return t('settings.changeLog.lastUpdatedNow')
    return t('settings.changeLog.lastUpdated', { time: relative })
  })()

  const entries = data?.results ?? []
  const showLegacyHistoryNotice = entries.some(entry =>
    predatesReliableChangeLog(entry.occurred_at)
  )
  const count = data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE))

  if (data && !isPlaceholderData) {
    knownTotalPagesRef.current = totalPages
  }

  const safePage = Math.min(page, knownTotalPagesRef.current)
  const rangeStart = count === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1
  const rangeEnd = count === 0 ? 0 : Math.min(safePage * PAGE_SIZE, count)

  const [resourceTypeLabels, setResourceTypeLabels] = useState<
    Partial<Record<ChangeLogResourceType, string>>
  >({})

  React.useEffect(() => {
    setResourceTypeLabels(prev => {
      const next = { ...prev }
      let changed = false
      for (const entry of entries) {
        if (next[entry.resource.type] !== entry.resource.type_label) {
          next[entry.resource.type] = entry.resource.type_label
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [entries])

  const getResourceTypeLabel = (type: ChangeLogResourceType | 'all'): string => {
    if (type === 'all') return t('settings.changeLog.filters.resourceTypeAll')
    return resourceTypeLabels[type] ?? humanizeResourceTypeSlug(type)
  }

  React.useEffect(() => {
    if (page > knownTotalPagesRef.current) {
      setPage(knownTotalPagesRef.current)
    }
  }, [page, data?.count])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const selectedActor = actors.find(actor => actor.id === actorId)
  const hasActiveFilters =
    resourceType !== 'all' ||
    action !== 'all' ||
    actorId != null ||
    Boolean(search.trim()) ||
    Boolean(from) ||
    Boolean(to)

  const { refs, floatingStyles, context } = useFloating({
    open: filtersOpen,
    onOpenChange: setFiltersOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip()]
  })
  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  const resetFilters = () => {
    setResourceType('all')
    setAction('all')
    setActorId(null)
    setSearch('')
    setFrom('')
    setTo('')
    setPage(1)
  }

  return (
    <SettingsSubPageLayout
      title={
        <TitleStack>
          <PageTitle>
            {t('settings.changeLog.mainTitle')}
            <TextTooltip tooltip={t('settings.changeLog.helpTooltip')} placement="bottom-start">
              <HelpIcon aria-label={t('settings.changeLog.helpTooltip')} />
            </TextTooltip>
          </PageTitle>
          {statusLabel ? (
            <LastUpdatedRow>
              <LastUpdated>{statusLabel}</LastUpdated>
              <RefreshIconButton
                type="button"
                aria-label={t('settings.changeLog.refresh')}
                disabled={isFetching}
                onClick={handleRefresh}
              >
                <FaSyncAlt className={isFetching ? 'spin' : undefined} />
              </RefreshIconButton>
            </LastUpdatedRow>
          ) : null}
        </TitleStack>
      }
      onBack={onBack}
      backAriaLabel={t('settings.changeLog.back')}
      contentPadding="none"
      contentOverflow="hidden"
      headerActions={
        <>
          {hasActiveFilters ? (
            <ActiveFilters>
              {[
                search.trim() || null,
                resourceType !== 'all' ? getResourceTypeLabel(resourceType) : null,
                action !== 'all' ? t(`settings.changeLog.actions.${action}`) : null,
                selectedActor
                  ? actorLabel(selectedActor, t('settings.changeLog.systemActor'))
                  : null,
                from ? `${t('settings.changeLog.filters.from')}: ${from}` : null,
                to ? `${t('settings.changeLog.filters.to')}: ${to}` : null
              ]
                .filter(Boolean)
                .join(' · ')}
            </ActiveFilters>
          ) : null}
          <FilterTriggerWrap>
            <Button
              ref={refs.setReference}
              type="button"
              isSecondary
              icon={<FaFilter />}
              aria-label={t('settings.changeLog.filters.open')}
              aria-expanded={filtersOpen}
              {...getReferenceProps()}
            />
            {hasActiveFilters ? <FilterActiveDot aria-hidden /> : null}
          </FilterTriggerWrap>
          <FloatingPortal>
            {filtersOpen ? (
              <FiltersPanel ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                <FiltersPanelTitle>{t('settings.changeLog.filters.title')}</FiltersPanelTitle>
                <div>
                  <Input
                    name="change-log-search"
                    label={t('settings.changeLog.filters.search')}
                    placeholder={t('settings.changeLog.filters.searchPlaceholder')}
                    value={search}
                    onChange={event => {
                      setSearch(event.target.value)
                      setPage(1)
                    }}
                    hideErrorText
                  />
                </div>
                <div>
                  <Select
                    name="change-log-resource-type"
                    label={t('settings.changeLog.filters.resourceType')}
                    value={resourceType}
                    onChange={event => {
                      setResourceType(event.target.value as ChangeLogResourceType | 'all')
                      setPage(1)
                    }}
                    hideErrorText
                  >
                    {RESOURCE_TYPES.map(type => (
                      <option key={type} value={type}>
                        {getResourceTypeLabel(type)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    name="change-log-action"
                    label={t('settings.changeLog.filters.action')}
                    value={action}
                    onChange={event => {
                      setAction(event.target.value as ChangeLogAction | 'all')
                      setPage(1)
                    }}
                    hideErrorText
                  >
                    {ACTIONS.map(value => (
                      <option key={value} value={value}>
                        {value === 'all'
                          ? t('settings.changeLog.filters.actionAll')
                          : t(`settings.changeLog.actions.${value}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    name="change-log-actor"
                    label={t('settings.changeLog.filters.user')}
                    value={actorId ?? ''}
                    onChange={event => {
                      const next = event.target.value
                      setActorId(next ? Number(next) : null)
                      setPage(1)
                    }}
                    hideErrorText
                  >
                    <option value="">{t('settings.changeLog.filters.userAll')}</option>
                    {actors.map(actor =>
                      actor.id != null ? (
                        <option key={actor.id} value={actor.id}>
                          {actorLabel(actor, t('settings.changeLog.systemActor'))}
                        </option>
                      ) : null
                    )}
                  </Select>
                </div>
                <div>
                  <Input
                    name="change-log-from"
                    label={t('settings.changeLog.filters.from')}
                    type="date"
                    value={from}
                    onChange={event => {
                      setFrom(event.target.value)
                      setPage(1)
                    }}
                    hideErrorText
                  />
                </div>
                <div>
                  <Input
                    name="change-log-to"
                    label={t('settings.changeLog.filters.to')}
                    type="date"
                    value={to}
                    onChange={event => {
                      setTo(event.target.value)
                      setPage(1)
                    }}
                    hideErrorText
                  />
                </div>
                {hasActiveFilters ? (
                  <ResetRow>
                    <Button type="button" isSecondary icon={<FaUndo />} onClick={resetFilters}>
                      {t('settings.changeLog.filters.reset')}
                    </Button>
                  </ResetRow>
                ) : null}
              </FiltersPanel>
            ) : null}
          </FloatingPortal>
        </>
      }
    >
      <PageBody>
        {showLegacyHistoryNotice && <LegacyHistoryNotice />}
        <TableScroll>
          <AnimatedContentLoader
            isLoading={isLoading}
            isEmpty={!isLoading && !isError && entries.length === 0}
            isEmptyDescription={t('settings.changeLog.empty')}
            isError={!isLoading && isError}
            errorDescription={t('settings.changeLog.loadError')}
          >
            <Table>
              <thead>
                <tr>
                  <th>{t('settings.changeLog.columns.time')}</th>
                  <th>{t('settings.changeLog.columns.user')}</th>
                  <th>{t('settings.changeLog.columns.action')}</th>
                  <th>{t('settings.changeLog.columns.object')}</th>
                  <th>{t('settings.changeLog.columns.changes')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id}>
                    <td className="time">{formatOccurredAt(entry.occurred_at)}</td>
                    <td className="user">
                      <ObjectCell>
                        {entry.actor.is_staff ? (
                          <TextTooltip tooltip={t('settings.changeLog.staffBadgeTooltip')}>
                            <ActorName>
                              {actorEmailFirst(entry, t('settings.changeLog.systemActor'))}
                              <StaffShield aria-hidden>
                                <FaShieldAlt />
                              </StaffShield>
                            </ActorName>
                          </TextTooltip>
                        ) : (
                          <ObjectLabel>
                            {actorEmailFirst(entry, t('settings.changeLog.systemActor'))}
                          </ObjectLabel>
                        )}
                        {entry.client ? (
                          <TextTooltip
                            tooltip={t(
                              CLIENT_I18N[entry.client]?.tooltip ??
                                'settings.changeLog.clients.tooltips.unknown',
                              { client: entry.client }
                            )}
                          >
                            <ClientBadge type="default">
                              {t(
                                CLIENT_I18N[entry.client]?.label ??
                                  'settings.changeLog.clients.unknown',
                                { client: entry.client }
                              )}
                            </ClientBadge>
                          </TextTooltip>
                        ) : null}
                      </ObjectCell>
                    </td>
                    <td>
                      <ActionBadges>
                        <ActionBadge type={ACTION_BADGE_TYPE[entry.action]}>
                          {t(`settings.changeLog.actions.${entry.action}`)}
                        </ActionBadge>
                        {entry.previous_untracked ? (
                          <TextTooltip tooltip={t('settings.changeLog.previousUntrackedTooltip')}>
                            <UntrackedBadge type="default">
                              {t('settings.changeLog.previousUntracked')}
                            </UntrackedBadge>
                          </TextTooltip>
                        ) : null}
                      </ActionBadges>
                    </td>
                    <td>
                      <ObjectCell>
                        <ObjectLabel>
                          <ResourceLabel slug={slug} resource={entry.resource} />
                        </ObjectLabel>
                        <ObjectMeta>{entry.resource.type_label}</ObjectMeta>
                      </ObjectCell>
                    </td>
                    <td>
                      {entry.changes.length === 0 ? (
                        <Muted>—</Muted>
                      ) : (
                        <ChangesStack>
                          {entry.changes.map(change => (
                            <FieldChange
                              key={`${entry.id}-${change.field}`}
                              change={change}
                              slug={slug}
                            />
                          ))}
                        </ChangesStack>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </AnimatedContentLoader>
        </TableScroll>

        <Footer>
          <PageSummary>
            {count === 0
              ? t('settings.changeLog.pagination.empty')
              : t('settings.changeLog.pagination.summary', {
                  start: rangeStart,
                  end: rangeEnd,
                  total: count
                })}
            {isFetching && !isLoading ? ' …' : null}
          </PageSummary>

          <Pagination
            page={safePage}
            totalPages={knownTotalPagesRef.current}
            onPageChange={setPage}
            prevLabel={t('settings.changeLog.pagination.prev')}
            nextLabel={t('settings.changeLog.pagination.next')}
          />
        </Footer>
      </PageBody>
    </SettingsSubPageLayout>
  )
}

export default ChangeLogSettings

const PageBody = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const LegacyHistoryNoticeBanner = styled.div`
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const TitleStack = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`

const PageTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 600;
  line-height: 1.3;
`

const LastUpdatedRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`

const LastUpdated = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.35;
`

const RefreshIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  border: none;
  border-radius: 0.6rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: pointer;
  line-height: 0;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: ${({ theme }) => theme.colors.neutralGray};
  }

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }

  svg {
    display: block;
    width: 0.65rem;
    height: 0.65rem;
    transform-origin: center center;
  }
`

const HelpIcon = styled(FaQuestionCircle)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  cursor: help;
`

const ActiveFilters = styled.div`
  max-width: 16rem;
  min-width: 0;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
`

const FilterTriggerWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`

const FilterActiveDot = styled.span`
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  pointer-events: none;
`

const FiltersPanel = styled.div`
  z-index: 20;
  width: min(20rem, calc(100vw - 2rem));
  padding: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const FiltersPanelTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: #1a1d21;
`

const ResetRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.xxs}rem;
`

const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.35;
  min-width: 920px;

  th {
    text-align: left;
    padding: 0.45rem 0.75rem;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.iconGray};
    background: #f8f9fb;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    padding: 0.5rem 0.75rem;
    vertical-align: top;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
    color: #1a1d21;
  }

  tbody tr:nth-child(odd) {
    background: #f8f9fb;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 8.5rem;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 13rem;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 6.5rem;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 12rem;
  }

  td.time {
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.iconGray};
  }

  td.user {
    font-weight: 500;
    color: #1a1d21;
  }
`

const ObjectCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
`

const ActionBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
`

const ActionBadge = styled(Badge)`
  font-size: 0.65rem;
  line-height: 1.2;

  /* Target BadgeContent padding */
  > div {
    padding: 0.05rem 0.35rem;
    gap: 0.2rem;
  }

  svg {
    width: 0.65rem;
    height: 0.65rem;
  }
`

const UntrackedBadge = styled(ActionBadge)`
  cursor: help;
`

const ClientBadge = styled(Badge)`
  width: fit-content;
  max-width: 100%;
  font-size: 0.65rem;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: help;

  > div {
    padding: 0.05rem 0.35rem;
    gap: 0.2rem;
  }
`

const ObjectLabel = styled.span`
  font-weight: 600;
  color: #1a1d21;
`

const ActorName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
  font-weight: 600;
  color: #1a1d21;
  cursor: help;
`

const StaffShield = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.AIPurple};
  line-height: 1;

  svg {
    width: 0.75rem;
    height: 0.75rem;
    display: block;
  }
`

const ObjectLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const ObjectMeta = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.iconGray};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const ChangesStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const Muted = styled.span`
  color: #a7afb8;
`

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.sm}rem ${theme.spacing.md}rem`};
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme }) => theme.colors.neutralWhite};
  flex-wrap: wrap;
  gap: 0.5rem;
`

const PageSummary = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`
